import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { fetchAccountManagers, fetchAccountManagersForAgency, fetchSalesRepresentatives } from "~/modules/Clients/api";
import { IAccountManager } from "~/modules/Clients/models/AccountManager";
import { ISalesRepresentative } from "~/modules/Clients/models/SalesRepresentative";
import { fetchDspConnectionTypes, fetchExternalBuyers, fetchExternalBuyersP2P, fetchPresets } from "~/modules/Dictionary/api";
import { IDspConnectionType } from "~/modules/Dictionary/models/DspConnectionType";
import { IExternalBuyer } from "~/modules/Dictionary/models/ExternalBuyer";
import { IExternalBuyerP2P } from "~/modules/Dictionary/models/ExternalBuyerP2P";
import { IPreset } from "~/modules/Dictionary/models/Preset";
import { staticQueryDefaults } from "~/lib/queryClient";

export const DSP_CONNECTION_TYPES_QUERY_KEY = ["dsp-connection-types"] as const;
export const useDspConnectionTypesQuery = (
  options?: Omit<UseQueryOptions<IDspConnectionType[]>, "queryKey" | "queryFn">
): UseQueryResult<IDspConnectionType[]> =>
  useQuery<IDspConnectionType[]>({
    queryKey: DSP_CONNECTION_TYPES_QUERY_KEY,
    queryFn: fetchDspConnectionTypes,
    ...staticQueryDefaults,
    ...options,
  });

export const ACCOUNT_MANAGERS_QUERY_KEY = ["account-managers"] as const;
export const useAccountManagersQuery = (
  options?: Omit<UseQueryOptions<IAccountManager[]>, "queryKey" | "queryFn">
): UseQueryResult<IAccountManager[]> =>
  useQuery<IAccountManager[]>({
    queryKey: ACCOUNT_MANAGERS_QUERY_KEY,
    queryFn: fetchAccountManagers,
    ...staticQueryDefaults,
    ...options,
  });

export const AGENCY_ACCOUNT_MANAGERS_QUERY_KEY = ["agency-managers"] as const;
export const useAgencyAccountManagersQuery = (
  options?: Omit<UseQueryOptions<IAccountManager[]>, "queryKey" | "queryFn">
): UseQueryResult<IAccountManager[]> =>
  useQuery<IAccountManager[]>({
    queryKey: AGENCY_ACCOUNT_MANAGERS_QUERY_KEY,
    queryFn: fetchAccountManagersForAgency,
    ...staticQueryDefaults,
    ...options,
  });

export const SALES_REPRESENTATIVES_QUERY_KEY = ["sales-representatives"] as const;
export const useSalesRepresentativesQuery = (
  options?: Omit<UseQueryOptions<ISalesRepresentative[]>, "queryKey" | "queryFn">
): UseQueryResult<ISalesRepresentative[]> =>
  useQuery<ISalesRepresentative[]>({
    queryKey: SALES_REPRESENTATIVES_QUERY_KEY,
    queryFn: fetchSalesRepresentatives,
    ...staticQueryDefaults,
    ...options,
  });

export const EXTERNAL_BUYERS_QUERY_KEY = ["external-buyers"] as const;
export const useExternalBuyersQuery = (
  options?: Omit<UseQueryOptions<IExternalBuyer[]>, "queryKey" | "queryFn">
): UseQueryResult<IExternalBuyer[]> =>
  useQuery<IExternalBuyer[]>({
    queryKey: EXTERNAL_BUYERS_QUERY_KEY,
    queryFn: fetchExternalBuyers,
    ...staticQueryDefaults,
    ...options,
  });

export const EXTERNAL_BUYERS_P2P_QUERY_KEY = ["external-buyers-p2p"] as const;
export const useExternalBuyersP2PQuery = (
  options?: Omit<UseQueryOptions<IExternalBuyerP2P[]>, "queryKey" | "queryFn">
): UseQueryResult<IExternalBuyerP2P[]> =>
  useQuery<IExternalBuyerP2P[]>({
    queryKey: EXTERNAL_BUYERS_P2P_QUERY_KEY,
    queryFn: fetchExternalBuyersP2P,
    ...staticQueryDefaults,
    ...options,
  });

export const PRESETS_QUERY_KEY = ["presets"] as const;
export const usePresetsQuery = (
  options?: Omit<UseQueryOptions<IPreset[]>, "queryKey" | "queryFn">
): UseQueryResult<IPreset[]> =>
  useQuery<IPreset[]>({
    queryKey: PRESETS_QUERY_KEY,
    queryFn: fetchPresets,
    ...staticQueryDefaults,
    ...options,
  });